import { Suspense, useRef, useCallback, useState, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera, Grid } from "@react-three/drei";
import VectorVisualization from "./VectorVisualization";
import DummyObject from "./DummyObject";

// Default camera position - Z pointing up, viewing from an elevated angle
const DEFAULT_CAMERA_POSITION = [40, 40, 30];
const DEFAULT_TARGET = [0, 0, 0];
const DEFAULT_CAMERA_UP = [0, 0, 1]; // Z axis pointing up

// Legend items matching VectorVisualization colors
const LEGEND_ITEMS = [
  { color: "#5cead4", label: "Source" },
  { color: "#98FAA3", label: "Direction to Source" },
  { color: "#94a3b8", label: "Detector" },
  { color: "#fbbf24", label: "X-Ray Path" },
  { color: "#FAC198", label: "U Vector" },
  { color: "#98FADB", label: "V Vector" },
  { color: "#CA98FA", label: "Direction to Detector" },
  { color: "#ffffff", label: "Rotation Axis" },
];

/**
 * Legend component showing color labels
 */
function Legend() {
  return (
    <div className="viewer-legend">
      {LEGEND_ITEMS.map((item, index) => (
        <div key={index} className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: item.color }}
          />
          <span className="legend-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Loading fallback component
 */
function LoadingFallback() {
  return (
    <mesh>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#5cead4" wireframe />
    </mesh>
  );
}

/**
 * Scene lighting setup
 */
function Lighting() {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={0.8} />
      <directionalLight position={[-10, -10, -5]} intensity={0.4} />
      <pointLight position={[0, 50, 0]} intensity={0.5} />
    </>
  );
}

/**
 * Ground grid for reference
 */
function GroundGrid() {
  return (
    <Grid
      position={[0, 0, 0]}
      rotation={[Math.PI / 2, 0, 0]}
      args={[100, 100]}
      cellSize={5}
      cellThickness={0.5}
      cellColor="#334155"
      sectionSize={20}
      sectionThickness={1}
      sectionColor="#475569"
      fadeDistance={200}
      fadeStrength={1}
      followCamera={false}
      infiniteGrid
    />
  );
}

/**
 * Camera controller component that handles reset
 */
function CameraController({ controlsRef, cameraRef }) {
  return (
    <>
      <PerspectiveCamera
        ref={cameraRef}
        makeDefault
        position={DEFAULT_CAMERA_POSITION}
        up={DEFAULT_CAMERA_UP}
        fov={50}
        near={0.1}
        far={1000}
      />
      <OrbitControls
        ref={controlsRef}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={10}
        maxDistance={500}
        target={DEFAULT_TARGET}
      />
    </>
  );
}

/**
 * Main 3D viewer component
 */
function Viewer3D({
  vectors,
  detectorWidth,
  detectorHeight,
  showFloor,
  setShowFloor,
  showAxisX,
  setShowAxisX,
  showAxisY,
  setShowAxisY,
  showAxisZ,
  setShowAxisZ,
  isAnimating,
  currentProjectionIndex,
  showDummy,
  dummyType,
  dummyColor,
  dummyCylinder,
  dummyPlane,
  showArrows,
  setShowArrows,
  showUVector,
  setShowUVector,
  showVVector,
  setShowVVector,
  showDirectionToSource,
  setShowDirectionToSource,
  showDirectionToDetector,
  setShowDirectionToDetector,
}) {
  const controlsRef = useRef();
  const cameraRef = useRef();
  const arrowControlsRef = useRef();
  const [showArrowSettings, setShowArrowSettings] = useState(false);
  const [showLegend, setShowLegend] = useState(true);

  const handleResetCamera = useCallback(() => {
    if (controlsRef.current && cameraRef.current) {
      // Reset camera position
      cameraRef.current.position.set(...DEFAULT_CAMERA_POSITION);
      // Reset camera up vector (Z pointing up)
      cameraRef.current.up.set(...DEFAULT_CAMERA_UP);
      // Reset orbit controls target
      controlsRef.current.target.set(...DEFAULT_TARGET);
      controlsRef.current.update();
    }
  }, []);

  // Close arrow settings panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (arrowControlsRef.current && !arrowControlsRef.current.contains(event.target)) {
        setShowArrowSettings(false);
      }
    };

    if (showArrowSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showArrowSettings]);

  return (
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Top Right Controls */}
      <div style={{ position: "absolute", top: "1rem", right: "1rem", zIndex: 20, display: "flex", gap: "6px" }}>
        <div style={{ display: "flex", gap: "0px" }}>
          <button
            className={`legend-toggle-btn ${showLegend ? "active" : ""}`}
            onClick={() => setShowLegend(!showLegend)}
            title={showLegend ? "Hide Legend" : "Show Legend"}
          >
            <svg width="15" height="15" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.25 1.75L1.96 2.85833C1.8375 2.89917 1.75 3.00417 1.75 3.13833V11.9583C1.75 12.0357 1.78073 12.1099 1.83543 12.1646C1.89013 12.2193 1.96431 12.25 2.04167 12.25L2.135 12.2325L5.25 11.025L8.75 12.25L12.04 11.1417C12.1625 11.1008 12.25 10.9958 12.25 10.8617V2.04167C12.25 1.96431 12.2193 1.89013 12.1646 1.83543C12.1099 1.78073 12.0357 1.75 11.9583 1.75L11.865 1.7675L8.75 2.975L5.25 1.75ZM4.66667 3.17917V10.0042L2.91667 10.6808V3.76833L4.66667 3.17917ZM5.83333 3.19083L8.16667 4.0075V10.8092L5.83333 9.9925V3.19083ZM11.0833 3.325V10.2317L9.33333 10.8208V4.00167L11.0833 3.325ZM4.35167 3.675L3.24917 4.06583V5.32L4.35167 4.92917V3.675ZM4.35167 5.27917L3.24917 5.67V6.92417L4.35167 6.53333V5.27917ZM4.35167 6.88333L3.24917 7.27417V8.52833L4.35167 8.1375V6.88333ZM4.35167 8.4875L3.24917 8.87833V10.1325L4.35167 9.74167V8.4875Z" fill="#a4b4cbff"/>
            </svg>
          </button>
          <button
            className={`floor-toggle-btn ${showFloor ? "active" : ""}`}
            onClick={() => setShowFloor(!showFloor)}
            title={showFloor ? "Hide Floor" : "Show Floor"}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 21H21V19H3V21ZM3 17H21V15H3V17ZM3 13H21V11H3V13ZM3 9H21V7H3V9ZM3 3V5H21V3H3Z" fill="#a4b4cbff"/>
            </svg>
          </button>
          <button
            className="reset-camera-btn"
            onClick={handleResetCamera}
            title="Reset Camera View"
          >
            <svg width="13" height="13" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_453_6)">
                <path d="M13.0986 2.52539C11.7275 0.978516 9.73242 0 7.5 0C3.35742 0 0 3.35742 0 7.5H1.875C1.875 4.3916 4.3916 1.875 7.5 1.875C9.21387 1.875 10.7344 2.65137 11.7627 3.86133L9.99902 5.625H15V0.624023L13.0986 2.52539ZM7.5 13.125C5.78613 13.125 4.26562 12.3486 3.2373 11.1387L5.00098 9.375H0V14.376L1.90137 12.4746C3.27246 14.0215 5.27051 15 7.5 15C11.6426 15 15 11.6426 15 7.5H13.125C13.125 10.6084 10.6084 13.125 7.5 13.125Z" fill="#a4b4cbff"/>
              </g>
              <defs>
                <clipPath id="clip0_453_6">
                  <rect width="15" height="15" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </button>
        </div>

        {/* Arrow Controls */}
        <div className="arrow-controls" ref={arrowControlsRef}>
          <button
            className={`arrow-toggle-btn ${showArrows ? "active" : ""}`}
            onClick={() => setShowArrows(!showArrows)}
            title={showArrows ? "Hide Guides" : "Show Guides"}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.7365 16.9864L16.9865 19.7364C16.8903 19.8327 16.7678 19.8982 16.6343 19.9248C16.5009 19.9514 16.3626 19.9378 16.2369 19.8857C16.1112 19.8336 16.0038 19.7454 15.9282 19.6322C15.8527 19.5191 15.8124 19.3861 15.8126 19.25V17.1875H6.87505C6.69272 17.1875 6.51785 17.1151 6.38892 16.9861C6.25999 16.8572 6.18755 16.6823 6.18755 16.5V6.87501H4.12505C3.989 6.87511 3.85597 6.83485 3.74282 6.75931C3.62966 6.68378 3.54145 6.57637 3.48938 6.45068C3.4373 6.32498 3.42368 6.18667 3.45025 6.05323C3.47682 5.9198 3.54239 5.79725 3.63865 5.7011L6.38865 2.9511C6.4525 2.88718 6.52832 2.83647 6.61178 2.80187C6.69524 2.76727 6.78471 2.74947 6.87505 2.74947C6.9654 2.74947 7.05486 2.76727 7.13832 2.80187C7.22179 2.83647 7.29761 2.88718 7.36146 2.9511L10.1115 5.7011C10.2077 5.79725 10.2733 5.9198 10.2999 6.05323C10.3264 6.18667 10.3128 6.32498 10.2607 6.45068C10.2087 6.57637 10.1204 6.68378 10.0073 6.75931C9.89413 6.83485 9.76111 6.87511 9.62505 6.87501H7.56255V15.8125H15.8126V13.75C15.8124 13.614 15.8527 13.4809 15.9282 13.3678C16.0038 13.2546 16.1112 13.1664 16.2369 13.1143C16.3626 13.0622 16.5009 13.0486 16.6343 13.0752C16.7678 13.1018 16.8903 13.1673 16.9865 13.2636L19.7365 16.0136C19.8004 16.0774 19.8511 16.1533 19.8857 16.2367C19.9203 16.3202 19.9381 16.4097 19.9381 16.5C19.9381 16.5904 19.9203 16.6798 19.8857 16.7633C19.8511 16.8467 19.8004 16.9226 19.7365 16.9864Z"
                fill="#a4b4cbff"
              />
            </svg>
          </button>
          <button
            className="arrow-settings-btn"
            onClick={() => setShowArrowSettings(!showArrowSettings)}
            title="Guide Settings"
          >
            <svg
              width="18"
              height="9"
              viewBox="0 0 18 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_452_4)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.46677 7.61776L4.22402 3.37501L5.28452 2.31451L8.99702 6.02701L12.7095 2.31451L13.77 3.37501L9.52727 7.61776C9.38662 7.75837 9.19589 7.83735 8.99702 7.83735C8.79815 7.83735 8.60741 7.75837 8.46677 7.61776Z"
                  fill="#a4b4cbff"
                />
              </g>
              <defs>
                <clipPath id="clip0_452_4">
                  <rect
                    width="9"
                    height="18"
                    fill="white"
                    transform="matrix(0 1 -1 0 18 0)"
                  />
                </clipPath>
              </defs>
            </svg>
          </button>

          {showArrowSettings && (
            <div className="arrow-settings-panel">
              <div className="arrow-settings-header">Guides</div>
              <label className="arrow-checkbox-label">
                <input
                  type="checkbox"
                  checked={showUVector}
                  onChange={(e) => setShowUVector(e.target.checked)}
                />
                <span>U Vector</span>
              </label>
              <label className="arrow-checkbox-label">
                <input
                  type="checkbox"
                  checked={showVVector}
                  onChange={(e) => setShowVVector(e.target.checked)}
                />
                <span>V Vector</span>
              </label>
              <label className="arrow-checkbox-label">
                <input
                  type="checkbox"
                  checked={showDirectionToSource}
                  onChange={(e) => setShowDirectionToSource(e.target.checked)}
                />
                <span>Direction to Source</span>
              </label>
              <label className="arrow-checkbox-label">
                <input
                  type="checkbox"
                  checked={showDirectionToDetector}
                  onChange={(e) => setShowDirectionToDetector(e.target.checked)}
                />
                <span>Direction to Detector</span>
              </label>

              {/* <div className="arrow-settings-divider"></div>
              <div className="arrow-settings-header">Axes</div> */}
              <div className="axes-settings-header">Axes</div>
              <div className="viewport-axis-buttons">
                <button
                  className={`viewport-axis-btn ${showAxisX ? "active" : ""}`}
                  onClick={() => setShowAxisX(!showAxisX)}
                >
                  X
                </button>
                <button
                  className={`viewport-axis-btn ${showAxisY ? "active" : ""}`}
                  onClick={() => setShowAxisY(!showAxisY)}
                >
                  Y
                </button>
                <button
                  className={`viewport-axis-btn ${showAxisZ ? "active" : ""}`}
                  onClick={() => setShowAxisZ(!showAxisZ)}
                >
                  Z
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showLegend && <Legend />}

      <Canvas style={{ background: "#0a0f1a" }} gl={{ antialias: true }}>
        <CameraController controlsRef={controlsRef} cameraRef={cameraRef} />

        <Suspense fallback={<LoadingFallback />}>
          <Lighting />
          {showFloor && <GroundGrid />}

          <VectorVisualization
            vectors={vectors}
            detectorWidth={detectorWidth}
            detectorHeight={detectorHeight}
            showAxisX={showArrows && showAxisX}
            showAxisY={showArrows && showAxisY}
            showAxisZ={showArrows && showAxisZ}
            isAnimating={isAnimating}
            currentProjectionIndex={currentProjectionIndex}
            showOrigin={!showDummy}
            showUVector={showArrows && showUVector}
            showVVector={showArrows && showVVector}
            showDirectionToSource={showArrows && showDirectionToSource}
            showDirectionToDetector={showArrows && showDirectionToDetector}
          />

          {showDummy && (
            <DummyObject
              type={dummyType}
              color={dummyColor}
              cylinder={dummyCylinder}
              plane={dummyPlane}
            />
          )}
        </Suspense>
      </Canvas>
    </div>
  );
}

export default Viewer3D;
